import { useState } from "react";
import { toast } from "sonner";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, ChevronDown } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const QUOTE_STATUSES = ["New", "In Review", "Quoted", "Won", "Closed"];
const FILTERS = ["All", ...QUOTE_STATUSES];
const LIMIT = 10;

export default function AdminQuotes() {
  const qc = useQueryClient();
  const [page, setPage] = useState(0);
  const [filter, setFilter] = useState("All");

  const { data, isLoading } = useQuery({
    queryKey: ["admin-quotes", page, filter],
    queryFn: async () => {
      const { data } = await api.get("/admin/quotes", {
        params: { skip: page * LIMIT, limit: LIMIT, status: filter },
      });
      return data;
    },
    keepPreviousData: true,
  });

  const mutation = useMutation({
    mutationFn: ({ id, status }) => api.patch(`/admin/quotes/${id}`, { status }),
    onSuccess: (res) => {
      toast.success(`${res.data.reference} → ${res.data.status}`);
      qc.invalidateQueries({ queryKey: ["admin-quotes"] });
    },
    onError: (err) => toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Update failed"),
  });

  const quotes = data?.items || [];
  const total = data?.total || 0;
  const pages = Math.max(1, Math.ceil(total / LIMIT));

  return (
    <div data-testid="admin-quotes-page">
      <div className="mb-8">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-primary mb-2">/ Admin</p>
        <h1 className="font-display font-extrabold text-3xl md:text-4xl tracking-tight">Manage Quotes</h1>
      </div>

      <div className="flex justify-between items-center mb-4">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid="quote-filter" className="flex items-center gap-2 border border-border bg-card px-4 py-2.5 font-mono text-xs uppercase tracking-wider min-w-[160px] hover:border-primary transition-colors">
              {filter} <ChevronDown size={14} className="ml-auto" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="rounded-none w-[160px]">
            {FILTERS.map((f) => (
              <DropdownMenuItem key={f} onClick={() => { setFilter(f); setPage(0); }} className="rounded-none font-mono text-xs uppercase tracking-wider">
                {f}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        <span className="font-mono text-[11px] text-muted-foreground">{total} total requests</span>
      </div>

      <div className="border border-border overflow-x-auto thin-scroll">
        <table className="w-full text-left border-collapse min-w-[820px]">
          <thead>
            <tr className="border-b border-border bg-muted/40">
              {["Reference", "Contact", "Service", "Assets", "Date", "Status"].map((h) => (
                <th key={h} className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground px-4 py-3">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={6} className="px-4 py-10 text-center font-mono text-xs text-muted-foreground">Loading…</td></tr>
            ) : quotes.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-10 text-center font-mono text-xs text-muted-foreground">No quotes found.</td></tr>
            ) : quotes.map((q) => (
              <tr key={q.id} data-testid="admin-quote-row" className="border-b border-border hover:bg-muted/30">
                <td className="px-4 py-3 font-mono text-sm text-primary">{q.reference}</td>
                <td className="px-4 py-3">
                  <div className="font-sans text-sm">{q.name}</div>
                  <div className="font-mono text-[11px] text-muted-foreground">{q.email}{q.company ? ` · ${q.company}` : ""}</div>
                </td>
                <td className="px-4 py-3 font-sans text-sm">{q.service}</td>
                <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{q.asset_count || "—"}</td>
                <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{new Date(q.created_at).toLocaleDateString()}</td>
                <td className="px-4 py-3">
                  <Select value={q.status} onValueChange={(v) => mutation.mutate({ id: q.id, status: v })}>
                    <SelectTrigger data-testid={`quote-status-${q.reference}`} className="w-[150px] rounded-none border-border bg-card h-9 font-mono text-[11px] uppercase tracking-wider focus:ring-primary/20">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="rounded-none">
                      {QUOTE_STATUSES.map((s) => (
                        <SelectItem key={s} value={s} className="rounded-none font-mono text-[11px] uppercase tracking-wider">{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between mt-4">
        <span className="font-mono text-[11px] text-muted-foreground">Page {page + 1} of {pages}</span>
        <div className="flex gap-2">
          <button data-testid="quotes-prev" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))} className="flex items-center gap-1 border border-border px-3 py-2 font-mono text-[11px] uppercase disabled:opacity-40 hover:border-primary transition-colors">
            <ChevronLeft size={14} /> Prev
          </button>
          <button data-testid="quotes-next" disabled={page + 1 >= pages} onClick={() => setPage((p) => p + 1)} className="flex items-center gap-1 border border-border px-3 py-2 font-mono text-[11px] uppercase disabled:opacity-40 hover:border-primary transition-colors">
            Next <ChevronRight size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}
